import { httpClient } from "@bw/shared/api";
import type {
  CourtResponse,
  FacilityResponse,
  FacilityPlaceClaimResponse,
  FacilityPlaceDecision,
  FacilityPlaceResponse,
  PlatformFacilityListResponse,
  PricingRuleResponse,
  Schema,
} from "@bw/shared/types/generated";

export type AuditQueryResponse = Schema["AuditQueryResponse"];

export type FacilityApprovalStatus =
  Schema["FacilityResponse"]["approval_status"];

export const FACILITY_APPROVAL_STATUSES = [
  "draft",
  "in_review",
  "approved",
  "rejected",
] as const satisfies readonly FacilityApprovalStatus[];

export type FacilityApprovalReadinessItem =
  Schema["FacilityApprovalReadinessItem"];
export type FacilityApprovalReadinessResponse =
  Schema["FacilityApprovalReadinessResponse"];

export interface ListPlatformFacilitiesParams {
  status?: string;
  approval_status?: FacilityApprovalStatus;
  org_id?: string;
  search?: string;
  limit?: number;
  offset?: number;
}

function facilitySegment(facilityId: string): string {
  return encodeURIComponent(facilityId);
}

export async function listPlatformFacilities(
  params?: ListPlatformFacilitiesParams,
): Promise<PlatformFacilityListResponse> {
  const sp = new URLSearchParams();
  if (params?.status) sp.set("status", params.status);
  if (params?.approval_status) {
    sp.set("approval_status", params.approval_status);
  }
  if (params?.org_id) sp.set("org_id", params.org_id);
  if (params?.search) sp.set("search", params.search);
  if (params?.limit !== undefined) sp.set("limit", String(params.limit));
  if (params?.offset !== undefined) sp.set("offset", String(params.offset));
  const qs = sp.toString();
  return httpClient.get<PlatformFacilityListResponse>(
    `/platform/facilities${qs ? `?${qs}` : ""}`,
  );
}

export async function getFacility(
  facilityId: string,
): Promise<FacilityResponse> {
  return httpClient.get<FacilityResponse>(
    `/facilities/${facilitySegment(facilityId)}`,
  );
}

export async function listFacilityCourts(
  facilityId: string,
): Promise<CourtResponse[]> {
  return httpClient.get<CourtResponse[]>(
    `/facilities/${facilitySegment(facilityId)}/courts`,
  );
}

export async function listFacilityPricingRules(
  facilityId: string,
): Promise<PricingRuleResponse[]> {
  return httpClient.get<PricingRuleResponse[]>(
    `/facilities/${facilitySegment(facilityId)}/pricing-rules`,
  );
}

export async function getFacilityAudit(
  facilityId: string,
): Promise<AuditQueryResponse> {
  const query = new URLSearchParams({
    resource_type: "facility",
    resource_id: facilityId,
    limit: "20",
  });
  return httpClient.get<AuditQueryResponse>(`/audit?${query.toString()}`);
}

export async function getFacilityApprovalReadiness(
  facilityId: string,
): Promise<FacilityApprovalReadinessResponse> {
  return httpClient.get<FacilityApprovalReadinessResponse>(
    `/platform/facilities/${facilitySegment(facilityId)}/approval-readiness`,
  );
}

export async function approveFacility(
  facilityId: string,
): Promise<FacilityResponse> {
  return httpClient.post<FacilityResponse>(
    `/platform/facilities/${facilitySegment(facilityId)}/approve`,
  );
}

export async function rejectFacility(
  facilityId: string,
  payload: Schema["FacilityApprovalRejectRequest"],
): Promise<FacilityResponse> {
  return httpClient.post<FacilityResponse>(
    `/platform/facilities/${facilitySegment(facilityId)}/reject`,
    payload,
  );
}

export async function setAssessmentCenterStatus(
  facilityId: string,
  payload: Schema["AssessmentCenterStatusUpdate"],
): Promise<FacilityResponse> {
  return httpClient.put<FacilityResponse>(
    `/platform/facilities/${facilitySegment(facilityId)}/assessment-center`,
    payload,
  );
}

export async function listFacilityPlaces(
  status: "pending" | "approved" | "rejected" = "pending",
): Promise<FacilityPlaceResponse[]> {
  return httpClient.get<FacilityPlaceResponse[]>(
    `/platform/facility-places?${new URLSearchParams({ status })}`,
  );
}

export async function decideFacilityPlace(
  placeId: string,
  decision: FacilityPlaceDecision,
): Promise<FacilityPlaceResponse> {
  return httpClient.post<FacilityPlaceResponse>(
    `/platform/facility-places/${facilitySegment(placeId)}/decision`,
    decision,
  );
}

export async function listFacilityPlaceClaims(
  status: "pending" | "approved" | "rejected" = "pending",
): Promise<FacilityPlaceClaimResponse[]> {
  return httpClient.get<FacilityPlaceClaimResponse[]>(
    `/platform/facility-place-claims?${new URLSearchParams({ status })}`,
  );
}

export async function decideFacilityPlaceClaim(
  claimId: string,
  decision: FacilityPlaceDecision,
): Promise<FacilityPlaceClaimResponse> {
  return httpClient.post<FacilityPlaceClaimResponse>(
    `/platform/facility-place-claims/${facilitySegment(claimId)}/decision`,
    decision,
  );
}
